import { ArrowRight, Sparkles, Shield, FileText, PawPrint, Home } from 'lucide-react';
import { motion, useInView } from 'motion/react';
import { useMemo, useRef, useState } from 'react';

import { PrivacyModal } from './PrivacyModal';
import { ImpressumModal } from './ImpressumModal';

export function Footer() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-120px' });
    const currentYear = new Date().getFullYear();

    const [privacyOpen, setPrivacyOpen] = useState(false);
    const [impressumOpen, setImpressumOpen] = useState(false);

    const currentPath = window.location.pathname;
    const isAnimalServicesPage = currentPath === '/webdesign-tierdienstleister';

    const links = useMemo(() => {
        if (isAnimalServicesPage) {
            return {
                company: [
                    { label: 'Startseite', href: '/', icon: Home },
                    { label: 'Beispiele', href: '#tierprojekte', icon: PawPrint },
                    { label: 'Leistungen', href: '#leistungen-tierdienstleister', icon: Sparkles },
                    { label: 'Ablauf', href: '#ablauf-tierdienstleister', icon: ArrowRight },
                    { label: 'Kontakt', href: '#kontakt', icon: ArrowRight },
                ],
            };
        }

        return {
            company: [
                { label: 'Über mich', href: '#ueber-mich' },
                { label: 'Dienstleistungen', href: '#dienstleistungen' },
                { label: 'Projekte', href: '#projekte' },
                { label: 'Kontakt', href: '#kontakt' },
            ],
        };
    }, [isAnimalServicesPage]);

    const footerContent = isAnimalServicesPage
        ? {
            badge: 'Spezialisiert auf Tierdienstleister',
            headline: 'Bereit für einen professionellen Auftritt in der Tierbranche?',
            subline:
                'Schreib mir kurz – ich prüfe deinen aktuellen Online-Auftritt und zeige dir konkrete Verbesserungen für mehr Sichtbarkeit und Anfragen.',
            cta: 'Website-Check anfragen',
            brandSubline: 'Websites für Hundefriseure & Tierdienstleister',
            description:
                'Webdesign für Hundefriseure, Hundeschulen, Hundezüchter und Tierbetriebe. Warm, professionell, mobil optimiert und auf Vertrauen sowie neue Anfragen ausgelegt.',
            made: 'Made for animal businesses',
        }
        : {
            badge: 'Schnell & unkompliziert',
            headline: 'Bereit fürs nächste Projekt?',
            subline: 'Schreib mir kurz – ich melde mich in der Regel innerhalb von 24 Stunden.',
            cta: 'Kontakt aufnehmen',
            brandSubline: 'Webentwicklung & Performance',
            description:
                'Professionelle Webentwicklung für Unternehmen, die mehr wollen. Moderne Lösungen, persönlicher Service und messbare Ergebnisse.',
            made: 'Made in Germany',
        };

    const handleFooterLinkClick = (e, href) => {
        if (!href) return;

        if (href.startsWith('/')) {
            return;
        }

        if (!href.startsWith('#')) return;

        e.preventDefault();

        const el = document.querySelector(href);

        if (!el) {
            window.location.href = `/${href}`;
            return;
        }

        const y = el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    return (
        <footer className="relative overflow-hidden px-4 pt-20 pb-10" ref={ref}>
            <div
                className={`absolute inset-0 ${
                    isAnimalServicesPage
                        ? 'bg-gradient-to-br from-emerald-950 via-stone-950 to-lime-950'
                        : 'bg-gradient-to-br from-gray-950 via-gray-950 to-purple-950'
                }`}
            />

            <div
                className="absolute -top-24 -right-24 h-[560px] w-[560px] rounded-full blur-3xl opacity-25"
                style={{
                    background: isAnimalServicesPage
                        ? 'radial-gradient(circle, rgba(132,204,22,0.38), transparent 60%)'
                        : 'radial-gradient(circle, rgba(168,85,247,0.38), transparent 60%)',
                }}
                aria-hidden="true"
            />

            <div
                className="absolute -bottom-36 -left-28 h-[620px] w-[620px] rounded-full blur-3xl opacity-20"
                style={{
                    background: isAnimalServicesPage
                        ? 'radial-gradient(circle, rgba(245,158,11,0.38), transparent 60%)'
                        : 'radial-gradient(circle, rgba(59,130,246,0.38), transparent 60%)',
                }}
                aria-hidden="true"
            />

            <div
                className="absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)',
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 40% 20%, black, transparent 70%)',
                }}
                aria-hidden="true"
            />

            {isAnimalServicesPage && (
                <>
                    <PawPrint className="pointer-events-none absolute left-[8%] top-20 h-14 w-14 rotate-[-18deg] text-white/5" />
                    <PawPrint className="pointer-events-none absolute right-[12%] bottom-28 h-16 w-16 rotate-[16deg] text-white/5" />
                </>
            )}

            <div className="relative z-10 mx-auto max-w-7xl text-white">
                <motion.div
                    className="mb-12 overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_24px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl"
                    initial={{ opacity: 0, y: 22 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="relative flex flex-col items-start justify-between gap-6 p-6 md:flex-row md:items-center md:p-8">
                        <div
                            className="pointer-events-none absolute inset-0 opacity-35"
                            style={{
                                background: isAnimalServicesPage
                                    ? 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 80% 0%, rgba(132,204,22,0.18), transparent 40%)'
                                    : 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 80% 0%, rgba(168,85,247,0.18), transparent 40%)',
                            }}
                            aria-hidden="true"
                        />

                        <div className="relative">
                            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs">
                                {isAnimalServicesPage ? (
                                    <PawPrint className="h-4 w-4 text-amber-300" />
                                ) : (
                                    <Sparkles className="h-4 w-4" />
                                )}
                                {footerContent.badge}
                            </div>

                            <div className="mt-3 text-2xl font-semibold leading-tight md:text-3xl">
                                {footerContent.headline}
                            </div>

                            <div className="mt-2 max-w-2xl text-white/75">
                                {footerContent.subline}
                            </div>
                        </div>

                        <a
                            href="#kontakt"
                            onClick={(e) => handleFooterLinkClick(e, '#kontakt')}
                            className="relative inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 font-medium text-gray-900 shadow-md transition-colors hover:bg-white/95"
                        >
                            {footerContent.cta}
                            <ArrowRight className="h-4 w-4" />
                        </a>
                    </div>
                </motion.div>

                <div className="grid gap-10 md:grid-cols-4">
                    <motion.div
                        className="md:col-span-2"
                        initial={{ opacity: 0, y: 22 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-white/10 shadow-md backdrop-blur">
                                <img
                                    src={`${process.env.PUBLIC_URL}/assets/LogoJITS.svg`}
                                    alt="Johannsen IT Logo"
                                    className="h-25 w-25"
                                    draggable="false"
                                />
                            </div>

                            <div>
                                <div className="text-lg font-semibold tracking-tight">
                                    Johannsen IT Solutions
                                </div>
                                <div className="text-sm text-white/70 -mt-0.5">
                                    {footerContent.brandSubline}
                                </div>
                            </div>
                        </div>

                        <p className="mt-5 max-w-xl leading-relaxed text-white/75">
                            {footerContent.description}
                        </p>

                        <div className="mt-5 inline-flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                            <span className="text-xs text-white/60">Direkt:</span>

                            <a
                                href="mailto:kontakt@johannsen-its.de"
                                className="text-sm font-medium text-white/85 underline underline-offset-4 hover:text-white"
                            >
                                kontakt@johannsen-its.de
                            </a>

                            <span className="text-white/30">•</span>

                            <a
                                href="tel:+4915117806720"
                                className="text-sm font-medium text-white/85 underline underline-offset-4 hover:text-white"
                            >
                                +49 151 17 80 6720
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 22 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.15, duration: 0.6 }}
                    >
                        <h4 className="mb-4 text-sm font-semibold tracking-wide text-white/90">
                            Navigation
                        </h4>

                        <ul className="space-y-2.5">
                            {links.company.map((link, index) => {
                                const Icon = link.icon;

                                return (
                                    <motion.li
                                        key={link.label}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                                        transition={{
                                            delay: 0.25 + 0.08 * index,
                                            duration: 0.35,
                                        }}
                                        whileHover={{ x: 4 }}
                                    >
                                        <a
                                            href={link.href}
                                            onClick={(e) => handleFooterLinkClick(e, link.href)}
                                            className="group inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
                                        >
                                            {Icon ? (
                                                <Icon className="h-4 w-4 opacity-70 transition-opacity group-hover:opacity-100" />
                                            ) : (
                                                <span className="h-1.5 w-1.5 rounded-full bg-white/20 transition-colors group-hover:bg-white/50" />
                                            )}
                                            {link.label}
                                        </a>
                                    </motion.li>
                                );
                            })}
                        </ul>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 22 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.25, duration: 0.6 }}
                    >
                        <h4 className="mb-4 text-sm font-semibold tracking-wide text-white/90">
                            Rechtliches
                        </h4>

                        <ul className="space-y-2.5">
                            <motion.li
                                initial={{ opacity: 0, x: -10 }}
                                animate={isInView ? { opacity: 1, x: 0 } : {}}
                                transition={{ delay: 0.35, duration: 0.35 }}
                                whileHover={{ x: 4 }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setImpressumOpen(true)}
                                    className="group inline-flex items-center gap-2 rounded text-white/70 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                                >
                                    <FileText className="h-4 w-4 opacity-70 transition-opacity group-hover:opacity-100" />
                                    Impressum
                                </button>
                            </motion.li>

                            <motion.li
                                initial={{ opacity: 0, x: -10 }}
                                animate={isInView ? { opacity: 1, x: 0 } : {}}
                                transition={{ delay: 0.43, duration: 0.35 }}
                                whileHover={{ x: 4 }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setPrivacyOpen(true)}
                                    className="group inline-flex items-center gap-2 rounded text-white/70 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                                >
                                    <Shield className="h-4 w-4 opacity-70 transition-opacity group-hover:opacity-100" />
                                    Datenschutz
                                </button>
                            </motion.li>
                        </ul>
                    </motion.div>
                </div>

                <motion.div
                    className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-8 text-sm text-white/60 md:flex-row"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ delay: 0.6, duration: 0.6 }}
                >
                    <p>&copy; {currentYear} Johannsen IT Solutions. Alle Rechte vorbehalten.</p>
                    <p className="text-white/50">{footerContent.made}</p>
                </motion.div>
            </div>

            <ImpressumModal open={impressumOpen} onClose={() => setImpressumOpen(false)} />
            <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
        </footer>
    );
}